// Need to use the React-specific entry point to allow generating React hooks
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Competition, Match as DomesticMatch, Match, Player } from '@uefadigital/panenka';
import QueryString from 'qs';

const paramsSerializer = (params: Record<string, any>) =>
  QueryString.stringify(params, {
    arrayFormat: 'comma',
    skipNulls: true,
    sort: (a, b) => a.localeCompare(b),
  });

import type {
  CompV2CompetitionStructure,
  CompV2TeamPlayers,
  CompetitionTeamSquad,
  CompstatsV1PlayerRankingLeader,
  CompstatsV1TeamRanking,
  CompstatsV1TeamStatisticsHead2Head,
  DomesticTeamsStanding,
  MatchCalendar,
  LineUps,
  MatchEvents,
  MatchStatistic,
  PlayerStatistic,
  Players,
  Ranking,
  Standing,
  CompstatsV1TeamStatisticsHead2HeadAdvanced,
} from 'types/ApiResponseInterfaces';
import { UEFA_BASE_URL } from 'utils/uefaBaseUrl';

type TeamStatisticsQueryArgs = {
  id: string;
  stats: string;
  offset: number;
  limit: number;
  seasonYear: string;
};

type TeamsForPlayersQueryArgs = {
  id: string;
  playerIds: Array<string>;
  seasonYear: string;
};

type PlayerStatisticsQueryArgs = {
  id: string;
  stats: string;
  limit: number;
  seasonYear: string;
};

type TeamsFromCompetitionQueryArgs = {
  id: string;
  seasonYear: string;
};

type MatchCalendarQueryArgs = {
  competitionIds: string | Array<string>;
  seasonYear: string;
  phase: string;
  startDate: string;
  endDate: string;
  utcOffset: number;
};

type HeadToHeadQueryArgs = {
  id: string | Array<string>;
  teamId: Array<string>;
};

type MatchHeadToHeadQueryArgs = {
  offset: number;
  limit: number;
  opponentTeamIds: Array<string>;
  toDate: string;
  competitionId: Array<string>;
};

type MatchHeadToHeadAdvancedQueryArgs = {
  offset: number;
  limit: number;
  matchId: string[];
};

type MatchEventsQueryArgs = {
  matchId: string;
  filter: string;
  offset: number;
  limit: number;
};

type RefereeMatchesQueryArgs = {
  refereeId: string;
  competitionId: string;
};

type MatchQueryArgs = {
  competitionId: string;
  seasonYear: string;
  phase: string;
  utcOffset: number;
  fromDate: string | undefined;
  toDate: string | undefined;
};

type MatchDataQueryArgs = {
  teamId: string | undefined;
  toDate: string | undefined;
};

type SquadCompetitionDataQueryArgs = {
  competitionId: string;
  seasonYear: string;
  teamId: string;
  phase?: string;
};

type PlayersStatisticQueryArgs = {
  competitionId: string;
  playerId: Array<string>;
  stats: Array<string>;
};

type PlayerStatusQueryArgs = {
  seasonYear: string;
  matchId: string;
  teamId: string;
  status: string;
};

type PlayersStatisticSeasonQueryArgs = PlayersStatisticQueryArgs & { seasonYear: string };

type DomesticTeamsStandingsQueryArgs = {
  competitionId: string;
};
type AllDomesticCompetitionsQueryArgs = {
  countryCode: string;
  gender: string;
};

export const setApiKeyHeader =
  (url = 'apikey') =>
  async (headers: Headers) => {
    const apiKey = await fetch(`${UEFA_BASE_URL}/thirdparties/template/${url}`).then(
      (res) => res.text(),
      () => null,
    );

    if (!apiKey) return headers;

    headers.append('X-Api-Key', apiKey);
    return headers;
  };

export const fspMatchApi = createApi({
  reducerPath: 'fspMatchApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_FSP_API_MATCH_BASE_URL,
    prepareHeaders: setApiKeyHeader(),
  }),
  endpoints: (builder) => ({
    getMatchById: builder.query<Match, string>({
      query: (id) => `/v5/matches/${id}`,
    }),
    getMatchCalendar: builder.query<MatchCalendar, MatchCalendarQueryArgs>({
      query: ({ competitionIds, seasonYear, phase, startDate, endDate, utcOffset }) =>
        `/v5/match-calendar?competitionIds=${competitionIds}&seasonYear=${seasonYear}&phase=${phase}&startDate=${startDate}&endDate=${endDate}&utcOffset=${utcOffset}`,
    }),
    getMatchHeadToHead: builder.query<Array<Match>, MatchHeadToHeadQueryArgs>({
      query: ({ offset, limit, opponentTeamIds, toDate, competitionId }) =>
        `/v5/matches?offset=${offset}&limit=${limit}&order=DESC&opponentTeamIds=${opponentTeamIds}&status=FINISHED&toDate=${toDate}&competitionId=${competitionId}`,
    }),
    getMatchHeadToHeadAdvanced: builder.query<Array<Match>, MatchHeadToHeadAdvancedQueryArgs>({
      query: ({ offset, limit, matchId }) =>
        `/v5/matches?offset=${offset}&limit=${limit}&order=DESC&matchId=${matchId}`,
    }),
    getMatchesInfoByTeamId: builder.query<Array<Match>, MatchDataQueryArgs>({
      query: ({ teamId, toDate }) =>
        `/v5/matches?status=FINISHED&teamId=${teamId}&toDate=${toDate}&order=DESC&offset=0&limit=5`,
    }),
    getLineUp: builder.query<LineUps, string>({
      query: (matchId) => `/v5/matches/${matchId}/lineups`,
    }),
    getMatchEvents: builder.query<Array<MatchEvents>, MatchEventsQueryArgs>({
      query: ({ matchId, filter, offset, limit }) =>
        `/v5/matches/${matchId}/events?filter=${filter}&offset=${offset}&limit=${limit}`,
    }),
    getMatches: builder.query<Array<Match>, MatchQueryArgs>({
      query: ({ competitionId, seasonYear, phase, fromDate, toDate, utcOffset }) =>
        `/v5/matches?competitionId=${competitionId}&seasonYear=${seasonYear}&phase=${phase}&fromDate=${fromDate}&toDate=${toDate}&utcOffset=${utcOffset}&order=ASC&offset=0&limit=500`,
    }),
    getMatchUnitById: builder.query<Array<Match>, Record<string, any>>({
      query: (params) =>
        `/v5/matches?${paramsSerializer({
          offset: 0,
          limit: 100,
          order: 'DESC',
          ...params,
        })}`,
      keepUnusedDataFor: 1,
    }),
    getRefereeMatchesById: builder.query<Array<Match>, RefereeMatchesQueryArgs>({
      query: ({ refereeId, competitionId }) =>
        `/v5/referee/${refereeId}/matches?limit=5&offset=0&competitionId=${competitionId}`,
    }),
  }),
});

export const fspCompApi = createApi({
  reducerPath: 'fspCompApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_FSP_API_COMP_BASE_URL,
    prepareHeaders: setApiKeyHeader(),
  }),
  endpoints: (builder) => ({
    getTeamsByCompetitionId: builder.query({
      query: (id) => `/v2/teams?competitionId=${id}`,
    }),
    getTeamsForPlayers: builder.query<Array<CompV2TeamPlayers>, TeamsForPlayersQueryArgs>({
      query: ({ id, playerIds, seasonYear }) =>
        `/v2/team-players?competitionId=${id}&playerId=${playerIds}&seasonYear=${seasonYear}&optionalFields=TEAM`,
    }),
    getCompetitionStructure: builder.query<Array<CompV2CompetitionStructure>, TeamsFromCompetitionQueryArgs>({
      query: ({ id, seasonYear }) => `/v2/competition-structure?competitionId=${id}&seasonYears=${seasonYear}`,
    }),
    getTeamsFromCompetition: builder.query({
      query: ({ id, seasonYear }) => `/v2/teams?competitionId=${id}&seasonYear=${seasonYear}&offset=0&limit=100`,
    }),
    getTeamsByCompetitionId2: builder.query({
      query: (competitionId) => `/v2/teams?competitionId=${competitionId}&offset=0&limit=300`,
    }),
    getTeamsByTeamId: builder.query({
      query: ({ teamIds }) => `/v2/teams?teamIds=${teamIds}`,
    }),
    getPlayerDetails: builder.query<Array<Player>, TeamsForPlayersQueryArgs>({
      query: ({ id, seasonYear, playerIds }) =>
        `/v2/players?competitionContext=${id}%3A${seasonYear}&playerIds=${playerIds}`,
    }),
    getTeamSquadForCompetition: builder.query<CompetitionTeamSquad, SquadCompetitionDataQueryArgs>({
      query: ({ competitionId, seasonYear, teamId }) =>
        `/v2/competitions/${competitionId}/seasons/${seasonYear}/teams/${teamId}/squad?playerLists=A_LIST,B_LIST`,
    }),
  }),
});

export const fspDomesticApi = createApi({
  reducerPath: 'fspDomesticApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_FSP_API_DOMESTIC_BASE_URL,
    prepareHeaders: setApiKeyHeader(),
  }),
  endpoints: (builder) => ({
    getMatchDomestic: builder.query<Array<DomesticMatch>, MatchDataQueryArgs>({
      query: ({ teamId, toDate }) =>
        `/v2/matches?status=FINISHED&teamId=${teamId}&toDate=${toDate}&order=DESC&offset=0&limit=5`,
    }),
    getDomesticStandings: builder.query<Array<DomesticTeamsStanding>, DomesticTeamsStandingsQueryArgs>({
      query: ({ competitionId }) => `/v2/standings?competitionId=${competitionId}&seasonYear=current`,
    }),
    getAllDomesticCompetitions: builder.query<Array<Competition>, AllDomesticCompetitionsQueryArgs>({
      query: ({ countryCode, gender }) => `/v2/competitions?countryCode=${countryCode}&sex=${gender}`,
    }),
  }),
});

export const fspStandingsApi = createApi({
  reducerPath: 'fspStandingsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_FSP_API_STANDINGS_BASE_URL,
    prepareHeaders: setApiKeyHeader(),
  }),
  endpoints: (builder) => ({
    getStandings: builder.query<Array<Standing>, SquadCompetitionDataQueryArgs>({
      query: ({ competitionId, seasonYear, teamId, phase }) =>
        `/v1/standings?competitionId=${competitionId}&seasonYear=${seasonYear}&teamIds=${teamId}&phase=${phase}`,
    }),
  }),
});

export const fspCompstatsApi = createApi({
  reducerPath: 'fspCompstatsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_FSP_API_COMPSTATS_BASE_URL,
    prepareHeaders: setApiKeyHeader(),
  }),
  endpoints: (builder) => ({
    getTeamStatisticsByType: builder.query<Array<CompstatsV1TeamRanking>, TeamStatisticsQueryArgs>({
      query: ({ id, stats, offset, limit, seasonYear }) =>
        `/v1/team-ranking?competitionId=${id}&stats=${stats}&offset=${offset}&limit=${limit}&seasonYear=${seasonYear}&optionalFields=TEAM&phase=TOURNAMENT`,
    }),
    getPlayerStatisticsByType: builder.query<Array<CompstatsV1PlayerRankingLeader>, PlayerStatisticsQueryArgs>({
      query: ({ id, stats, limit, seasonYear }) =>
        `/v1/player-ranking-leader?competitionId=${id}&stats=${stats}&limit=${limit}&seasonYear=${seasonYear}&optionalFields=PLAYER&phase=TOURNAMENT`,
    }),
    getHeadToHead: builder.query<Array<CompstatsV1TeamStatisticsHead2Head>, HeadToHeadQueryArgs>({
      query: ({ id, teamId }) => `/v1/team-statistics/head2head?competitionId=${id}&teamId=${teamId}`,
    }),
    getHeadToHeadAdvanced: builder.query<Array<CompstatsV1TeamStatisticsHead2HeadAdvanced>, HeadToHeadQueryArgs>({
      query: ({ id, teamId }) => `/v1/team-statistics/head2head-advanced?competitionIds=${id}&teamIds=${teamId}`,
    }),
    getPlayersStatisticForCompetition: builder.query<Array<PlayerStatistic>, PlayersStatisticQueryArgs>({
      query: ({ competitionId, stats, playerId }) =>
        `/v1/player-statistics?competitionId=${competitionId}&playerId=${playerId}&stats=${stats}`,
    }),
    getPlayersSeasonCompetitionStatistic: builder.query<Array<PlayerStatistic>, PlayersStatisticSeasonQueryArgs>({
      query: ({ competitionId, stats, playerId, seasonYear }) =>
        `/v1/player-statistics?competitionId=${competitionId}&playerId=${playerId}&stats=${stats}&seasonYear=${seasonYear}`,
    }),
    getPlayerAllUefaCompetition: builder.query<Array<PlayerStatistic>, PlayersStatisticQueryArgs>({
      query: ({ competitionId, stats, playerId }) =>
        `/v1/player-statistics?competitionId=${competitionId}&playerId=${playerId}&stats=${stats}`,
    }),
  }),
});

export const fspMatchstatsApi = createApi({
  reducerPath: 'fspMatchstatsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_FSP_API_MATCHSTATS_BASE_URL,
    prepareHeaders: setApiKeyHeader(),
  }),
  endpoints: (builder) => ({
    getMatchStatistic: builder.query<Array<MatchStatistic>, string>({
      query: (matchId) => `/v1/team-statistics/${matchId}`,
    }),
    getPlayerAllTimeStatisticsByType: builder.query<Array<Ranking>, string>({
      query: (matchId) => `/v1/player-statistics/${matchId}?optionalFields=PLAYER`,
    }),
  }),
});

export const fspPssApi = createApi({
  reducerPath: 'fspPssApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_FSP_API_PSS_BASE_URL,
    prepareHeaders: setApiKeyHeader(),
  }),
  endpoints: (builder) => ({
    getPlayerStatus: builder.query<Players, PlayerStatusQueryArgs>({
      query: ({ seasonYear, matchId, teamId, status }) =>
        `/v3/playerstatus?seasonYear=${seasonYear}&teamId=${teamId}&matchId=${matchId}&status=${status}`,
    }),
  }),
});
// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints

export const { useGetPlayerStatusQuery } = fspPssApi;

export const { useGetMatchStatisticQuery, useGetPlayerAllTimeStatisticsByTypeQuery } = fspMatchstatsApi;

export const {
  useGetMatchByIdQuery,
  useGetMatchCalendarQuery,
  useGetMatchHeadToHeadQuery,
  useGetMatchHeadToHeadAdvancedQuery,
  useGetMatchesQuery,
  useGetMatchesInfoByTeamIdQuery,
  useGetLineUpQuery,
  useGetMatchEventsQuery,
  useGetMatchUnitByIdQuery,
  useGetRefereeMatchesByIdQuery,
} = fspMatchApi;

export const {
  useGetTeamsForPlayersQuery,
  useGetTeamsByCompetitionIdQuery,
  useGetCompetitionStructureQuery,
  useGetTeamsFromCompetitionQuery,
  useGetTeamsByCompetitionId2Query,
  useGetTeamsByTeamIdQuery,
  useGetPlayerDetailsQuery,
  useGetTeamSquadForCompetitionQuery,
} = fspCompApi;

export const { useGetMatchDomesticQuery, useGetDomesticStandingsQuery, useGetAllDomesticCompetitionsQuery } =
  fspDomesticApi;

export const { useGetStandingsQuery } = fspStandingsApi;

export const {
  useGetTeamStatisticsByTypeQuery,
  useGetPlayerStatisticsByTypeQuery,
  useGetHeadToHeadQuery,
  useGetHeadToHeadAdvancedQuery,
  useGetPlayersStatisticForCompetitionQuery,
  useGetPlayersSeasonCompetitionStatisticQuery,
  useGetPlayerAllUefaCompetitionQuery,
} = fspCompstatsApi;
